import { Link, useParams } from 'react-router-dom';
import { api, getToken } from '../lib/api';
import type { ApkVersion } from '../lib/api';
import {
  Empty,
  ErrorNotice,
  Loading,
  Notice,
  Panel,
  formatBytes,
  formatDateTime,
  useAsync,
} from '../components/ui';

interface Detail {
  application: {
    id: string;
    package_name: string;
    display_name: string;
    description: string;
    signing_cert_sha256: string | null;
    signature_pinned_at: string | null;
    created_at: string;
  };
  versions: ApkVersion[];
}

export default function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, reload } = useAsync(
    (signal) => api.get<Detail>(`/applications/${id}`, signal),
    [id],
  );

  const download = async (version: ApkVersion) => {
    // Downloads stream through the authenticated backend; the storage bucket is
    // private and never exposed to the browser.
    const response = await fetch(`/api/apk-versions/${version.id}/download`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${data!.application.package_name}-${version.version_name}.apk`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const remove = async (version: ApkVersion) => {
    if (!window.confirm(`Delete version ${version.version_name}? This removes the stored APK file.`)) return;
    try {
      await api.delete(`/apk-versions/${version.id}`);
      reload();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  if (loading && !data) return <Loading what="application" />;
  if (error) return <ErrorNotice error={error} />;
  if (!data) return <Empty>Application not found.</Empty>;

  const app = data.application;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>{app.display_name}</h2>
          <p className="mono">{app.package_name} · <Link to="/applications">back to applications</Link></p>
        </div>
      </div>

      <Panel title="Application">
        <dl className="kv">
          <dt>Package</dt><dd className="mono">{app.package_name}</dd>
          <dt>Signing certificate</dt>
          <dd className="mono small">
            {app.signing_cert_sha256 ?? 'not pinned'}
            {app.signature_pinned_at && (
              <div className="muted">Pinned {formatDateTime(app.signature_pinned_at)}</div>
            )}
          </dd>
          <dt>Versions</dt><dd>{data.versions.length}</dd>
          <dt>First uploaded</dt><dd>{formatDateTime(app.created_at)}</dd>
        </dl>
        {app.signing_cert_sha256 && (
          <Notice kind="info">
            Uploads for this package must be signed with the same certificate. Android refuses to
            update an application whose signing key has changed, so a mismatched upload is
            rejected here rather than failing later on every tablet.
          </Notice>
        )}
      </Panel>

      <Panel title="Versions">
        {data.versions.length === 0 ? (
          <Empty>No versions uploaded.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Version</th><th className="num">Code</th><th>Min SDK</th>
                  <th>Size</th><th>SHA-256</th><th>Signature</th>
                  <th className="num">Deployments</th><th>Uploaded</th><th />
                </tr>
              </thead>
              <tbody>
                {data.versions.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <strong>v{v.version_name}</strong>
                      {v.notes && <div className="small muted">{v.notes}</div>}
                    </td>
                    <td className="num">{v.version_code}</td>
                    <td>
                      {v.min_sdk_version ?? '—'}
                      {v.min_sdk_version !== null && v.min_sdk_version > 24 && (
                        <div className="small" style={{ color: 'var(--danger)' }}>
                          Cannot install on Android 7
                        </div>
                      )}
                    </td>
                    <td>{formatBytes(v.file_size_bytes)}</td>
                    <td className="mono small" title={v.sha256}>{v.sha256.slice(0, 16)}…</td>
                    <td>
                      <span className="badge muted">{v.signature_schemes.join(', ') || 'none'}</span>
                      {!v.signature_schemes.includes('v1') && (
                        <div className="small" style={{ color: 'var(--warn)' }}>no v1 — Android 7 risk</div>
                      )}
                    </td>
                    <td className="num">{v.deployment_count}</td>
                    <td className="small muted">
                      {formatDateTime(v.created_at)}
                      {v.uploaded_by_email && <div>{v.uploaded_by_email}</div>}
                    </td>
                    <td>
                      <div className="actions">
                        <Link className="button small" to={`/deployments?apk=${v.id}`}>Deploy</Link>
                        <button className="small" onClick={() => download(v)}>Download</button>
                        {v.deployment_count === 0 && (
                          <button className="small danger" onClick={() => remove(v)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="small muted" style={{ marginBottom: 0 }}>
          A version that has been deployed cannot be deleted: deployment history must stay
          traceable to the exact file that was installed.
        </p>
      </Panel>
    </>
  );
}
